import React, { ComponentType, JSX, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { PUBLIC_HOME_COPY as COPY } from 'lib/publicHomeCopy'

export const TRIGGER_SHIP_ART: readonly string[] = [
  '.......OOOOO.',
  '.....ROBBBBO.',
  '.......OBBO..',
  '....OOOBBBOO.',
  '..OOBBBBBBBBO',
  'OOCCCBLLRBBRR',
  '..OOBBBBBBBBO',
  '....OOOBBBOO.',
  '.......OBBO..',
  '.....ROBBBBO.',
  '.......OOOOO.'
]

export const TRIGGER_SHIP_PALETTE: Readonly<Record<string, string>> = {
  O: '#1b2a6b',
  B: '#2f62e8',
  L: '#7fa8ff',
  C: '#a5f3fc',
  R: '#ff3b30'
}

const GAME_BOX = 'pointer-events-none absolute inset-x-0 top-0 z-30 aspect-video sm:bottom-0 sm:right-[max(40%,220px)] sm:aspect-auto'

const TRIGGER = 'pointer-events-auto absolute right-2 top-[56%] inline-flex size-11 items-center justify-center rounded-lg opacity-60 transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:right-1 sm:top-1/2 sm:-translate-y-1/2'

const ShipGlyph = (): JSX.Element => {
  const cells = TRIGGER_SHIP_ART.flatMap((row, y) =>
    [...row].flatMap((ch, x) => {
      const fill = TRIGGER_SHIP_PALETTE[ch]
      return fill == null ? [] : [<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill={fill} />]
    })
  )
  return (
    <svg
      aria-hidden='true'
      focusable='false'
      width={TRIGGER_SHIP_ART[0].length * 2}
      height={TRIGGER_SHIP_ART.length * 2}
      viewBox={`0 0 ${TRIGGER_SHIP_ART[0].length} ${TRIGGER_SHIP_ART.length}`}
      shapeRendering='crispEdges'
    >
      {cells}
    </svg>
  )
}

interface GameProps {
  onClose: () => void
  paused?: boolean
  best?: number
  onBest?: (score: number) => void
}

const GameUnavailable = ({ onClose }: GameProps): null => {
  useEffect(() => {
    onClose()
  }, [onClose])
  return null
}

const LazyFlightDeckGame = lazy(async (): Promise<{ default: ComponentType<GameProps> }> => {
  try {
    const loaded = await import('components/publicHome/FlightDeckGame')
    return { default: loaded.FlightDeckGame }
  } catch {
    return { default: GameUnavailable }
  }
})

export const FlightDeckGameLauncher = ({
  open,
  onOpenChange,
  paused = false
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  paused?: boolean
}): JSX.Element => {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const returnFocus = useRef(false)
  const [best, setBest] = useState(0)

  const recordScore = useCallback((score: number): void => {
    setBest((was) => Math.max(was, score))
  }, [])

  const close = useCallback((): void => {
    returnFocus.current = true
    onOpenChange(false)
  }, [onOpenChange])

  useEffect(() => {
    if (open || !returnFocus.current) return
    returnFocus.current = false
    triggerRef.current?.focus()
  }, [open])

  return (
    <div data-testid='flightdeck-game-box' className={GAME_BOX}>
      <button
        ref={triggerRef}
        type='button'
        data-testid='flightdeck-game-trigger'
        aria-label={COPY.game.trigger}
        aria-expanded={open}
        title={COPY.game.trigger}
        onClick={() => onOpenChange(true)}
        className={`${TRIGGER} ${open ? 'invisible' : ''}`}
      >
        <ShipGlyph />
      </button>
      {open && (
        <Suspense fallback={null}>
          <LazyFlightDeckGame onClose={close} paused={paused} best={best} onBest={recordScore} />
        </Suspense>
      )}
    </div>
  )
}

export const FlightDeckGameBlurb = ({ className = '' }: { className?: string }): JSX.Element => (
  <div data-testid='flightdeck-game-blurb' className={`text-left ${className}`}>
    <h2 className='text-lg font-semibold leading-6 text-dm-text-primary'>{COPY.game.heading}</h2>
    <p className='mt-1 text-sm leading-5 text-dm-text-secondary'>{COPY.game.body}</p>
    <p className='mt-2 text-sm font-semibold leading-5 text-dm-text-primary'>{COPY.game.prompt}</p>
    <p className='mt-1 text-xs leading-[18px] text-dm-text-accent'>{COPY.game.pointerHint}</p>
  </div>
)
